import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { SIGNAL_SLOT_LABEL } from '@indipia/shared'
import type { SignalItem, TaskSignal } from '@indipia/shared'
import { Button, SignalDot, TextLink } from '@indipia/ui-core'
import { Z_CONTEXT_MENU } from '../constants/zIndex'
import { SIGNAL_COLOR_LABEL, SIGNAL_SLOTS, hasSignalAction, slotItem } from '../constants/signalView'

// 신호 사유 팝오버 — 간트 막대 옆 점 3개·캘린더 막대를 눌러 연다 (940 시트4 ④ 'C-1 간트 신호등').
// 줄마다 신호 점 · 신호 이름 · 상태명 · 사유 문장 · 할일 제목 + 처리 버튼 1개, 아래에 [할일 보기 ›].
// 문구 · 할일 · 버튼 · 이동 주소는 서버(SIG-01)가 완성해 내려준 값을 그대로 쓴다. 할일 코드는 보이지 않는다.
// 팝오버 기반은 ui-core Popover 가 없어 .menu 계열과 같은 토큰(면·테두리·그림자)으로 직접 그린다.

const POPOVER_WIDTH = 340

interface Props {
  /** 화면 좌표(viewport) — 열린 지점. 화면 밖으로 나가지 않게 안으로 당긴다 */
  anchor: { x: number; y: number }
  workTypeName: string
  task: TaskSignal
  onClose: () => void
  /** 이 버튼의 주소로 갈 수 있는지 — 간트 주소는 화면이 열고, 그 밖은 호스트 이동이 있어야 한다(없으면 버튼 숨김) */
  canAct: (item: SignalItem) => boolean
  /** 처리 버튼 — 서버가 내려준 주소(webPath)·호출 주소(actionUrl)로 처리한다 */
  onAct: (item: SignalItem) => void
  /** [할일 보기 ›] — 같은 화면의 오늘 할일 패널을 연다. 할일 번호가 있는 줄이 있을 때만 보인다 */
  onOpenTodos?: () => void
  /** [간트로 보기 ›] — 캘린더에서 같은 공종을 간트로 본다. 없으면 숨긴다 */
  onOpenGantt?: () => void
}

export function SignalPopover({ anchor, workTypeName, task, onClose, canAct, onAct, onOpenTodos, onOpenGantt }: Props) {
  const boxRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: anchor.x, top: anchor.y })

  // 화면 밖으로 나가지 않게 보정
  useLayoutEffect(() => {
    const el = boxRef.current
    const h = el?.offsetHeight ?? 0
    const left = Math.max(8, Math.min(anchor.x, window.innerWidth - POPOVER_WIDTH - 8))
    const top = anchor.y + h + 8 > window.innerHeight ? Math.max(8, anchor.y - h - 8) : anchor.y
    setPos({ left, top })
  }, [anchor.x, anchor.y, task])

  useEffect(() => {
    const onDown = (e: MouseEvent) => { if (!boxRef.current?.contains(e.target as Node)) onClose() }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey) }
  }, [onClose])

  const hasTodo = SIGNAL_SLOTS.some(s => slotItem(task, s)?.todoName)

  return (
    <div
      ref={boxRef}
      role="dialog"
      aria-label={`${workTypeName} 신호`}
      onMouseDown={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
      style={{
        position: 'fixed', left: pos.left, top: pos.top, width: POPOVER_WIDTH, zIndex: Z_CONTEXT_MENU,
        background: 'var(--shell-surface)', border: 'var(--border-width-hairline) solid var(--shell-border)',
        borderRadius: 12, boxShadow: 'var(--elevation-2)', padding: '10px 12px',
        cursor: 'default',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <b style={{ flex: 1, minWidth: 0, fontSize: 13, color: 'var(--shell-on-surface)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{workTypeName}</b>
        <Button variant="ghost" size="sm" aria-label="닫기" onClick={onClose}>×</Button>
      </div>

      {SIGNAL_SLOTS.map((slot, i) => {
        const item = slotItem(task, slot)
        if (!item) return null
        const name = item.reasonName ?? null
        const line = item.reasonText ?? item.reasonLine ?? null
        const todoName = item.todoName ?? null
        const showAct = hasSignalAction(item) && canAct(item)
        return (
          <div key={slot} style={{ padding: '8px 0', borderTop: i === 0 ? 'none' : 'var(--border-width-hairline) solid var(--shell-border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <SignalDot color={item.colorCode} label={`${SIGNAL_SLOT_LABEL[slot]} 신호 ${SIGNAL_COLOR_LABEL[item.colorCode]}`} />
              <span style={{ fontSize: 12, color: 'var(--shell-on-surface-2)', flexShrink: 0 }}>{SIGNAL_SLOT_LABEL[slot]}</span>
              {name && <b style={{ fontSize: 12, color: 'var(--shell-on-surface)' }}>{name}</b>}
            </div>
            {name ? (
              line && (
                <div style={{ fontSize: 12, color: 'var(--shell-on-surface-2)', marginTop: 3, paddingLeft: 16, lineHeight: 1.5 }}>{line}</div>
              )
            ) : (
              <div role="alert" style={{ fontSize: 12, color: 'var(--status-danger-600)', marginTop: 3, paddingLeft: 16 }}>표시할 수 없는 신호 상태예요</div>
            )}
            {(todoName || showAct) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5, paddingLeft: 16 }}>
                {todoName && <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: 'var(--shell-on-surface)' }}>{todoName}</span>}
                {!todoName && <span style={{ flex: 1 }} />}
                {showAct && (
                  <Button variant="secondary" size="sm" onClick={() => onAct(item)}>{item.actionName}</Button>
                )}
              </div>
            )}
          </div>
        )
      })}

      {((hasTodo && onOpenTodos) || onOpenGantt) && (
        <div style={{ borderTop: 'var(--border-width-hairline) solid var(--shell-border)', paddingTop: 8, display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          {onOpenGantt && <TextLink onClick={onOpenGantt}>간트로 보기 ›</TextLink>}
          {hasTodo && onOpenTodos && <TextLink onClick={onOpenTodos}>할일 보기 ›</TextLink>}
        </div>
      )}
    </div>
  )
}
